import { useCallback } from 'react';
import { ReactNode, useState } from 'react';
import { useContext, createContext } from 'react';

import { toast } from 'react-toastify';

import api from '~/services/api';

type Category = {
  id: number;
  professional_id: number;
  category: string;
  created_at: string;
  updated_at: string;
  description: string;
};

type TrainingStoreData = {
  categoryData: Category[];
  updateCategory: (categoryName: string) => void;
  getAdminCategory: (categoryName: string) => void;
  deleteCategoryItem: (category: Category) => void;
  editCategoryItem: (category: Category) => void;
};

export const TrainingStoreContext = createContext({} as TrainingStoreData);

type Props = {
  children: ReactNode;
};

export function TrainingStoreProvider({ children }: Props) {
  const [categoryData, setCategoryData] = useState<Category[]>([]);

  const updateCategory = useCallback(async (categoryName: string) => {
    const { data } = await api.get<Category[]>(`training-stores`, {
      params: {
        segment: categoryName,
      },
    });

    setCategoryData(data);
  }, []);

  const getAdminCategory = useCallback(async (categoryName: string) => {
    try {
      const { data } = await api.get<Category[]>(
        `/training-stores/list/admin`,
        {
          params: {
            segment: categoryName,
          },
        },
      );

      setCategoryData(data);
    } catch (error) {
      console.log('error:', error);
      toast.error('Houve um erro ao buscar os exercícios do Administrador');
    }
  }, []);

  async function deleteCategoryItem(category: Category) {
    try {
      await api.delete(`training-stores/${category.id}`);

      const filteredCategory = categoryData.filter(
        (category_) => category_.id !== category.id,
      );

      setCategoryData(filteredCategory);

      toast.success(`Item "${category.description}" deletado com sucesso!`);
    } catch (err) {
      console.log(err);

      toast.error(
        `Ocorreu um erro ao tentar deletar o item "${category.description}!"`,
      );
    }
  }

  async function editCategoryItem(category: Category) {
    const data = {
      category: category.category,
      description: category.description,
    };

    try {
      await api.put(`training-stores/${category.id}`, data);

      const filteredCategory = categoryData.filter(
        (category_) => category_.id !== category.id,
      );
      const categoryDataSorted = [...filteredCategory, category].sort(
        (a, b) => {
          const descA = Number.isNaN(Number(a.description))
            ? a.description.toString()
            : String(a.description).padStart(10, '0');

          const descB = Number.isNaN(Number(b.description))
            ? b.description.toString()
            : String(b.description).padStart(10, '0');

          return descA.localeCompare(descB, undefined, { numeric: true });
        },
      );

      setCategoryData(categoryDataSorted);

      toast.success(`Item "${category.description}" editado com sucesso!`);
    } catch (err) {
      console.log(err);

      toast.error(
        `Ocorreu um erro ao tentar editar o item "${category.description}!"`,
      );
    }
  }

  return (
    <TrainingStoreContext.Provider
      value={{
        categoryData,
        updateCategory,
        getAdminCategory,
        deleteCategoryItem,
        editCategoryItem,
      }}
    >
      {children}
    </TrainingStoreContext.Provider>
  );
}

export function useTrainingStore(): TrainingStoreData {
  const context = useContext(TrainingStoreContext);

  if (!context) {
    throw new Error(
      `useTrainingStore must be use within a TrainingStoreProvider!`,
    );
  }

  return context;
}
